import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, Plus, X, Calendar, Trash2, CheckCircle, AlertCircle, Unlink } from 'lucide-react';
import { initiateCalendarConnect, disconnectCalendar, getCalendarStatus } from '../../services/calendar-service';
import { initiateZoomConnect, disconnectZoom, getZoomStatus, saveRecordingPreference } from '../../services/zoom-service';
import firestoreService from '../../services/firestore-service';
import { useToast } from '../../components/ToastNotification';
import LoadingButton from '../../components/common/LoadingButton';
import { auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { useScrollToHash } from '../../utils/useScrollToHash';

const DAYS = [
  { id: 'monday', label: 'Monday', short: 'Mon' },
  { id: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { id: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { id: 'thursday', label: 'Thursday', short: 'Thu' },
  { id: 'friday', label: 'Friday', short: 'Fri' },
  { id: 'saturday', label: 'Saturday', short: 'Sat' },
  { id: 'sunday', label: 'Sunday', short: 'Sun' },
];

const TIME_OPTIONS = [];
for (let h = 5; h <= 23; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
    const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    TIME_OPTIONS.push({ value, label });
  }
}

const DEFAULT_BLOCKS = [
  { start: '09:00', end: '12:00' },
  { start: '13:00', end: '17:00' },
];

const RECORDING_PREF_LABELS = {
  auto: 'Auto-record my sessions',
  ask: 'Ask me before each session',
  none: "Don't record sessions",
};

const RECORDING_PREF_OPTIONS = [
  { value: 'auto', label: 'Auto-record my sessions (you can still skip individual ones)' },
  { value: 'ask', label: 'Ask me before each session' },
  { value: 'none', label: "Don't record sessions" },
];

const AvailabilitySettings = () => {
  useScrollToHash();
  const toast = useToast();

  // Load from localStorage (mock - replace with Firestore in production)
  const [availability, setAvailability] = useState(() => {
    const saved = localStorage.getItem('practitionerAvailability');
    return saved ? JSON.parse(saved) : {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      schedule: {},
      bufferTime: 15,
      bookingWindow: 30,
      minNotice: 24,
      calendarType: null,
      calendarConnected: false,
      calendarProvider: null,
      timeOff: []
    };
  });

  const [isSaving, setIsSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_calendarMessage, _setCalendarMessage] = useState('');
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);

  // Google Calendar integration state
  const [calendarStatus, setCalendarStatus] = useState(null); // null = loading, { connected, connectedAt }
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [isCalendarConnecting, setIsCalendarConnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [calendarDisconnecting, setCalendarDisconnecting] = useState(false);
  // Prevents double-toast in React 18 Strict Mode (effect fires twice in dev)
  const calendarToastShownRef = useRef(false);

  // Zoom integration state
  const [zoomStatus, setZoomStatus] = useState(null); // null = loading, { connected, connectedAt }
  const [zoomLoading, setZoomLoading] = useState(true);
  const [isZoomConnecting, setIsZoomConnecting] = useState(false);
  const [showZoomDisconnectConfirm, setShowZoomDisconnectConfirm] = useState(false);
  const [zoomDisconnecting, setZoomDisconnecting] = useState(false);
  const zoomToastShownRef = useRef(false);

  // Recording preference state
  const [zoomRecordingPref, setZoomRecordingPref] = useState(null); // 'auto' | 'ask' | 'none' | null
  const [recordingPrefDraft, setRecordingPrefDraft] = useState('ask');
  const [showRecordingSetup, setShowRecordingSetup] = useState(false); // first-time setup prompt
  const [showRecordingEdit, setShowRecordingEdit] = useState(false);   // editing existing pref
  const [savingRecordingPref, setSavingRecordingPref] = useState(false);

  // Buffer state
  const [bufferTime, setBufferTime] = useState(availability.bufferTime || 15);
  const [isCustomBuffer, setIsCustomBuffer] = useState(false);
  const [customBufferValue, setCustomBufferValue] = useState(15);
  const [customBufferUnit, setCustomBufferUnit] = useState('minutes');

  // Booking Window state
  const [bookingWindow, setBookingWindow] = useState(availability.bookingWindow || 30);
  const [isCustomBooking, setIsCustomBooking] = useState(false);
  const [customBookingValue, setCustomBookingValue] = useState(30);
  const [customBookingUnit, setCustomBookingUnit] = useState('days');

  // Minimum Notice state
  const [minNotice, setMinNotice] = useState(availability.minNotice || 24);
  const [isCustomNotice, setIsCustomNotice] = useState(false);
  const [customNoticeValue, setCustomNoticeValue] = useState(24);
  const [customNoticeUnit, setCustomNoticeUnit] = useState('hours');

  // Time Off modal state
  const [timeOffTitle, setTimeOffTitle] = useState('');
  const [timeOffStart, setTimeOffStart] = useState('');
  const [timeOffEnd, setTimeOffEnd] = useState('');

  // Background: sync from Firestore if localStorage is empty
  useEffect(() => {
    const saved = localStorage.getItem('practitionerAvailability');
    if (!saved) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getAvailability(userId).then(firestoreData => {
          if (firestoreData && Object.keys(firestoreData).length > 0) {
            // Cache to localStorage for next time
            localStorage.setItem('practitionerAvailability', JSON.stringify(firestoreData));
            // Update component state
            setAvailability(firestoreData);
            if (firestoreData.bufferTime !== undefined) setBufferTime(firestoreData.bufferTime);
            if (firestoreData.bookingWindow !== undefined) setBookingWindow(firestoreData.bookingWindow);
            if (firestoreData.minNotice !== undefined) setMinNotice(firestoreData.minNotice);
          }
        }).catch(err => console.warn('Firestore availability read failed:', err));
      }
    }
  }, []);

  // Load Google Calendar status + handle OAuth return params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarParam = params.get('calendar');

    // After OAuth redirect we already know the answer from the URL param.
    // Set optimistic state and return early — no need to start an auth listener
    // that could overwrite the status before Firestore propagates the write.
    if (calendarParam === 'connected' && !calendarToastShownRef.current) {
      calendarToastShownRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      setCalendarStatus({ connected: true, connectedAt: null });
      setCalendarLoading(false);
      toast.success('Calendar connected', 'Google Calendar connected successfully!');
      return;
    }

    if (calendarParam === 'error' && !calendarToastShownRef.current) {
      calendarToastShownRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      setCalendarLoading(false);
      toast.error('Connection failed', 'Could not connect Google Calendar. Please try again.');
      return;
    }

    // Normal page load: wait for Firebase Auth to restore the session, then
    // read Firestore. onAuthStateChanged fires immediately with null when the
    // session hasn't been read from IndexedDB yet — we must NOT stop loading or
    // unsubscribe on null; we keep the listener alive until a real user arrives.
    let done = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (!user) return; // Session still restoring — keep waiting

      done = true;
      unsubscribe();
      getCalendarStatus(user.uid)
        .then(status => {
          if (status) setCalendarStatus(status);
          setCalendarLoading(false);
        })
        .catch(() => setCalendarLoading(false));
    });

    // Safety valve: stop spinning after 3 seconds if auth never resolves
    const timeout = setTimeout(() => {
      if (!done) setCalendarLoading(false);
    }, 3000);

    return () => {
      done = true;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Zoom status + handle OAuth return param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zoomParam = params.get('zoom');

    if (zoomParam === 'connected' && !zoomToastShownRef.current) {
      zoomToastShownRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      setZoomStatus({ connected: true, connectedAt: null });
      setZoomLoading(false);
      setShowRecordingSetup(true);
      setRecordingPrefDraft('ask');
      toast.success('Zoom connected', 'Your Zoom account is now connected!');
      return;
    }

    if (zoomParam === 'error' && !zoomToastShownRef.current) {
      zoomToastShownRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      setZoomLoading(false);
      toast.error('Connection failed', 'Could not connect Zoom. Please try again.');
      return;
    }

    let done = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (!user) return;

      done = true;
      unsubscribe();
      getZoomStatus(user.uid)
        .then(status => {
          if (status) {
            setZoomStatus(status);
            setZoomRecordingPref(status.recordingPreference || null);
            if (!status.recordingPreference) {
              // Pref not set yet — show setup prompt
              setShowRecordingSetup(true);
              setRecordingPrefDraft('ask');
            }
          }
          setZoomLoading(false);
        })
        .catch(() => setZoomLoading(false));
    });

    const timeout = setTimeout(() => {
      if (!done) setZoomLoading(false);
    }, 3000);

    return () => {
      done = true;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectGoogleCalendar = async () => {
    const uid = auth?.currentUser?.uid || firestoreService.getCurrentUserId();
    if (!uid) {
      toast.error('Error', 'Could not identify your account. Please sign in again.');
      return;
    }
    setIsCalendarConnecting(true);
    try {
      await initiateCalendarConnect(uid);
      // Browser will redirect — no further code runs here
    } catch (err) {
      toast.error('Connection failed', 'Could not start Google Calendar connection. Please try again.');
      setIsCalendarConnecting(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    const uid = firestoreService.getCurrentUserId();
    if (!uid) return;
    setCalendarDisconnecting(true);
    try {
      await disconnectCalendar(uid);
      setCalendarStatus(null);
      setShowDisconnectConfirm(false);
      toast.success('Disconnected', 'Google Calendar has been disconnected.');
    } catch (err) {
      toast.error('Error', 'Could not disconnect calendar. Please try again.');
    } finally {
      setCalendarDisconnecting(false);
    }
  };

  const handleConnectZoom = async () => {
    const uid = auth?.currentUser?.uid || firestoreService.getCurrentUserId();
    if (!uid) {
      toast.error('Error', 'Could not identify your account. Please sign in again.');
      return;
    }
    setIsZoomConnecting(true);
    try {
      await initiateZoomConnect(uid);
      // Browser will redirect — no further code runs here
    } catch (err) {
      toast.error('Connection failed', 'Could not start Zoom connection. Please try again.');
      setIsZoomConnecting(false);
    }
  };

  const handleDisconnectZoom = async () => {
    const uid = firestoreService.getCurrentUserId();
    if (!uid) return;
    setZoomDisconnecting(true);
    try {
      await disconnectZoom(uid);
      setZoomStatus(null);
      setZoomRecordingPref(null);
      setShowRecordingSetup(false);
      setShowRecordingEdit(false);
      setShowZoomDisconnectConfirm(false);
      toast.success('Disconnected', 'Zoom has been disconnected.');
    } catch (err) {
      toast.error('Error', 'Could not disconnect Zoom. Please try again.');
    } finally {
      setZoomDisconnecting(false);
    }
  };

  const handleSaveRecordingPref = async () => {
    const uid = auth?.currentUser?.uid || firestoreService.getCurrentUserId();
    if (!uid) return;
    setSavingRecordingPref(true);
    try {
      await saveRecordingPreference(uid, recordingPrefDraft);
      setZoomRecordingPref(recordingPrefDraft);
      setShowRecordingSetup(false);
      setShowRecordingEdit(false);
      toast.success('Saved', 'Recording preference updated.');
    } catch (err) {
      toast.error('Error', 'Could not save recording preference. Please try again.');
    } finally {
      setSavingRecordingPref(false);
    }
  };

  // Auto-save when session settings change
  useEffect(() => {
    const updated = {
      ...availability,
      bufferTime,
      bookingWindow,
      minNotice
    };
    setAvailability(updated);

    // Save to localStorage (instant)
    localStorage.setItem('practitionerAvailability', JSON.stringify(updated));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveAvailability(userId, updated).catch(err => {
        console.warn('⚠️ Firestore availability save failed:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bufferTime, bookingWindow, minNotice]);

  const handleSave = async () => {
    setIsSaving(true);

    // Save to localStorage (instant)
    localStorage.setItem('practitionerAvailability', JSON.stringify(availability));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveAvailability(userId, availability).then(() => {
        console.log('✅ Availability saved to Firestore');
      }).catch(err => {
        console.warn('⚠️ Firestore availability save failed:', err);
      });
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    setIsSaving(false);
    toast.success('Saved', 'Availability settings saved.');
  };

  const updateField = (field, value) => {
    const updated = { ...availability, [field]: value };
    setAvailability(updated);
  };

  const updateSchedule = (newSchedule) => {
    updateField('schedule', newSchedule);
  };

  const toggleDay = (dayId) => {
    const schedule = availability.schedule || {};
    const dayData = schedule[dayId];
    if (dayData?.enabled) {
      updateSchedule({
        ...schedule,
        [dayId]: { enabled: false, blocks: [] },
      });
    } else {
      updateSchedule({
        ...schedule,
        [dayId]: { enabled: true, blocks: [...DEFAULT_BLOCKS] },
      });
    }
  };

  const addBlock = (dayId) => {
    const schedule = availability.schedule || {};
    const dayData = schedule[dayId];
    const blocks = dayData?.blocks || [];
    const lastBlock = blocks[blocks.length - 1];
    const newStart = lastBlock ? lastBlock.end : '09:00';
    const newEnd = newStart === '17:00' ? '18:00' : '17:00';
    updateSchedule({
      ...schedule,
      [dayId]: { ...dayData, blocks: [...blocks, { start: newStart, end: newEnd }] },
    });
  };

  const removeBlock = (dayId, blockIndex) => {
    const schedule = availability.schedule || {};
    const dayData = schedule[dayId];
    const blocks = [...(dayData?.blocks || [])];
    blocks.splice(blockIndex, 1);
    if (blocks.length === 0) {
      updateSchedule({
        ...schedule,
        [dayId]: { enabled: false, blocks: [] },
      });
    } else {
      updateSchedule({
        ...schedule,
        [dayId]: { ...dayData, blocks },
      });
    }
  };

  const updateBlockTime = (dayId, blockIndex, field, value) => {
    const schedule = availability.schedule || {};
    const dayData = schedule[dayId];
    const blocks = [...(dayData?.blocks || [])];
    blocks[blockIndex] = { ...blocks[blockIndex], [field]: value };
    updateSchedule({
      ...schedule,
      [dayId]: { ...dayData, blocks },
    });
  };

  const handleAddTimeOff = () => {
    if (!timeOffStart || !timeOffEnd) return;

    const newTimeOff = {
      id: Date.now().toString(),
      title: timeOffTitle.trim() || 'Time Off',
      start: timeOffStart,
      end: timeOffEnd,
    };

    const updated = {
      ...availability,
      timeOff: [...(availability.timeOff || []), newTimeOff]
    };
    setAvailability(updated);
    localStorage.setItem('practitionerAvailability', JSON.stringify(updated));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveAvailability(userId, updated).catch(err => {
        console.warn('⚠️ Firestore availability save failed:', err);
      });
    }

    // Reset modal
    setTimeOffTitle('');
    setTimeOffStart('');
    setTimeOffEnd('');
    setShowTimeOffModal(false);
  };

  const handleDeleteTimeOff = (id) => {
    const updated = {
      ...availability,
      timeOff: (availability.timeOff || []).filter(t => t.id !== id)
    };
    setAvailability(updated);
    localStorage.setItem('practitionerAvailability', JSON.stringify(updated));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveAvailability(userId, updated).catch(err => {
        console.warn('⚠️ Firestore availability save failed:', err);
      });
    }
  };

  const schedule = availability.schedule || {};
  const timeOff = availability.timeOff || [];

  // Sort time off by start date
  const sortedTimeOff = [...timeOff].sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Availability & Scheduling</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Availability & Scheduling</h1>
          <p className="text-gray-600">Manage when you're available for sessions</p>
        </div>

        <div className="space-y-6">
          {/* Section 1: Timezone */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timezone</h2>
            <p className="text-sm text-gray-600 mb-4">
              We'll show clients times in their local timezone
            </p>
            <select
              value={availability.timezone}
              onChange={(e) => updateField('timezone', e.target.value)}
              className="w-full max-w-md px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none transition-all bg-white"
            >
              <option value="">Select timezone...</option>
              <option value="Pacific/Honolulu">Hawaii (HST)</option>
              <option value="America/Anchorage">Alaska (AKST)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Puerto_Rico">Atlantic (AST)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Central European (CET)</option>
              <option value="Europe/Athens">Eastern European (EET)</option>
              <option value="Asia/Dubai">Dubai (GST)</option>
              <option value="Asia/Kolkata">India (IST)</option>
              <option value="Asia/Singapore">Singapore (SGT)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Australia/Sydney">Sydney (AEST)</option>
              <option value="Pacific/Auckland">New Zealand (NZST)</option>
            </select>
            {availability.timezone && (
              <p className="text-xs text-gray-400 mt-2">
                Current: {availability.timezone.replace(/_/g, ' ')}
              </p>
            )}
          </div>

          {/* Section 2: Calendar Integration */}
          <div id="google-calendar-integration" className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Calendar Integration</h2>
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
              Connect your Google Calendar to automatically sync bookings and block off busy times.
            </p>

            {/* Google Calendar card */}
            <div className={`rounded-lg border p-4 mb-4 transition-colors ${
              calendarStatus?.connected
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {/* Google logo */}
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-7 h-7">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    {calendarLoading ? (
                      <p className="text-sm font-medium text-gray-500">Checking status...</p>
                    ) : calendarStatus?.connected ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-semibold text-green-700">Google Calendar Connected</span>
                        </div>
                        {calendarStatus.connectedAt && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Connected {calendarStatus.connectedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-gray-800">Google Calendar</p>
                        <p className="text-xs text-gray-500 mt-0.5">Sync bookings automatically</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Action button */}
                {!calendarLoading && (
                  calendarStatus?.connected ? (
                    <button
                      type="button"
                      onClick={() => setShowDisconnectConfirm(true)}
                      className="text-sm text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1.5"
                    >
                      <Unlink className="w-3.5 h-3.5" />
                      Disconnect
                    </button>
                  ) : (
                    <LoadingButton
                      onClick={handleConnectGoogleCalendar}
                      loading={isCalendarConnecting}
                      loadingText="Connecting…"
                      className="text-sm font-medium text-white px-4 py-2 rounded-lg transition-opacity flex-shrink-0"
                      style={{ backgroundColor: '#7c3aed' }}
                    >
                      Connect Google Calendar
                    </LoadingButton>
                  )
                )}
              </div>
            </div>

            {/* Phase 2 placeholders */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: 'Apple Calendar',
                  icon: (
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  ),
                },
                {
                  label: 'Outlook',
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.58.23h-8.547v-6.959l1.6 1.229c.102.086.227.127.373.127.14 0 .263-.04.367-.122l.062-.05 6.725-5.489.007.024c.153.14.231.327.231.56zM24 5.5c0 .086-.012.166-.035.24l-7.402 6.05-2.928-2.29V4.136c0-.226.08-.418.24-.573.158-.156.35-.234.577-.234h8.968c.228 0 .422.078.58.234zM7.325 7.99L1.052 4.19C1.225 3.498 1.69 3 2.35 3h12.064c.347 0 .646.113.897.34l-7.986 5.65zM8.004 16.248v-5.396l6.21-4.406c.26.21.388.488.388.833v10.22c0 .346-.125.64-.375.884-.25.243-.55.365-.9.365H2.35c-.698 0-1.185-.38-1.35-1.09l7.004-4.41z"/>
                    </svg>
                  ),
                },
              ].map((cal) => (
                <div
                  key={cal.label}
                  className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 opacity-60"
                >
                  {cal.icon}
                  <div>
                    <span className="text-xs font-medium text-gray-500">{cal.label}</span>
                    <p className="text-xs text-gray-400">Coming soon</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section: Zoom Integration */}
          <div id="zoom-integration" className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Zoom Integration</h2>
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
              Connect your Zoom account to automatically create meeting links for every new booking.
            </p>

            {!zoomLoading && !zoomStatus?.connected && (
              <div className="mb-5 p-3 rounded-md bg-gray-50 border border-gray-200">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <span className="font-semibold text-gray-900">Free Zoom</span> works for 1-on-1 sessions of any length. <span className="font-semibold text-gray-900">Zoom Pro</span> is needed for group sessions over 40 minutes, automatic recordings, and AI-generated session summaries. <a href="https://zoom.us/pricing" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline font-medium">Compare Zoom plans →</a>
                </p>
              </div>
            )}

            {/* Zoom card */}
            <div className={`rounded-lg border p-4 transition-colors ${
              zoomStatus?.connected
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {/* Zoom logo */}
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="40" height="40" rx="8" fill="#2D8CFF"/>
                      <path d="M23 16v8L31 20l-8-4z" fill="white"/>
                      <rect x="9" y="13" width="14" height="14" rx="2.5" fill="white"/>
                    </svg>
                  </div>
                  <div>
                    {zoomLoading ? (
                      <p className="text-sm font-medium text-gray-500">Checking status...</p>
                    ) : zoomStatus?.connected ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-semibold text-green-700">Zoom Connected</span>
                        </div>
                        {zoomStatus.connectedAt && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Connected {zoomStatus.connectedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        {!zoomStatus.connectedAt && (
                          <p className="text-xs text-gray-500 mt-0.5">Zoom meetings are automatically created for new bookings.</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-gray-800">Zoom</p>
                        <p className="text-xs text-gray-500 mt-0.5">Auto-create meeting links for bookings</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Action button */}
                {!zoomLoading && (
                  zoomStatus?.connected ? (
                    <button
                      type="button"
                      onClick={() => setShowZoomDisconnectConfirm(true)}
                      className="text-sm text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1.5"
                    >
                      <Unlink className="w-3.5 h-3.5" />
                      Disconnect
                    </button>
                  ) : (
                    <LoadingButton
                      onClick={handleConnectZoom}
                      loading={isZoomConnecting}
                      loadingText="Connecting…"
                      className="text-sm font-medium text-white px-4 py-2 rounded-lg transition-opacity flex-shrink-0"
                      style={{ backgroundColor: '#7c3aed' }}
                    >
                      Connect Zoom
                    </LoadingButton>
                  )
                )}
              </div>
            </div>

            {/* Recording preference — shown when Zoom is connected */}
            {zoomStatus?.connected && !zoomLoading && (
              <div className="mt-4">
                {(showRecordingSetup || !RECORDING_PREF_LABELS[zoomRecordingPref]) ? (
                  /* First-time setup or pref not yet set */
                  <div className="rounded-lg border border-purple-100 bg-purple-50/40 p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-0.5">Session Recording</p>
                    <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                      Recorded sessions can be transcribed and summarized by AI, giving you structured notes with key themes, action items, and follow-ups.
                    </p>
                    <p className="text-xs font-medium text-gray-700 mb-2">How would you like to handle recording?</p>
                    <div className="flex items-start gap-2 mb-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <span className="text-amber-700 text-sm flex-shrink-0">⚠️</span>
                      <p className="text-xs text-amber-900 leading-relaxed">
                        <span className="font-semibold">Auto-record requires Zoom Pro.</span> Free Zoom only supports local recordings saved to your device, which DF can't access for transcripts or AI summaries. <a href="https://zoom.us/pricing" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700">Compare Zoom plans →</a>
                      </p>
                    </div>
                    <div className="space-y-2 mb-4">
                      {RECORDING_PREF_OPTIONS.map(option => (
                        <label key={option.value} className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="recordingPrefSetup"
                            value={option.value}
                            checked={recordingPrefDraft === option.value}
                            onChange={() => setRecordingPrefDraft(option.value)}
                            className="mt-0.5 accent-purple-600"
                          />
                          <span className="text-sm text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveRecordingPref}
                      disabled={savingRecordingPref}
                      className="text-sm font-medium text-white px-4 py-2 rounded-lg transition-opacity disabled:opacity-60"
                      style={{ backgroundColor: '#7c3aed' }}
                    >
                      {savingRecordingPref ? 'Saving…' : 'Save Preference'}
                    </button>
                  </div>
                ) : showRecordingEdit ? (
                  /* Edit existing preference */
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-medium text-gray-700 mb-2">Recording Preference</p>
                    <div className="flex items-start gap-2 mb-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <span className="text-amber-700 text-sm flex-shrink-0">⚠️</span>
                      <p className="text-xs text-amber-900 leading-relaxed">
                        <span className="font-semibold">Auto-record requires Zoom Pro.</span> Free Zoom only supports local recordings saved to your device, which DF can't access for transcripts or AI summaries. <a href="https://zoom.us/pricing" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700">Compare Zoom plans →</a>
                      </p>
                    </div>
                    <div className="space-y-2 mb-3">
                      {RECORDING_PREF_OPTIONS.map(option => (
                        <label key={option.value} className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="recordingPrefEdit"
                            value={option.value}
                            checked={recordingPrefDraft === option.value}
                            onChange={() => setRecordingPrefDraft(option.value)}
                            className="mt-0.5 accent-purple-600"
                          />
                          <span className="text-sm text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveRecordingPref}
                        disabled={savingRecordingPref}
                        className="text-sm font-medium text-white px-3 py-1.5 rounded-lg transition-opacity disabled:opacity-60"
                        style={{ backgroundColor: '#7c3aed' }}
                      >
                        {savingRecordingPref ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRecordingEdit(false)}
                        className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display current preference */
                  <div className="flex items-center justify-between px-1 py-1.5">
                    <div>
                      <span className="text-xs text-gray-500">Recording: </span>
                      <span className="text-xs font-medium text-gray-700">
                        {RECORDING_PREF_LABELS[zoomRecordingPref]}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setRecordingPrefDraft(zoomRecordingPref || 'ask');
                        setShowRecordingEdit(true);
                      }}
                      className="text-xs text-purple-600 hover:text-purple-700 font-medium transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Zoom disconnect confirmation modal */}
          {showZoomDisconnectConfirm && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowZoomDisconnectConfirm(false)}
            >
              <div
                className="bg-white rounded-xl p-6 shadow-xl"
                style={{ maxWidth: '400px', width: '100%' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Disconnect Zoom?</h3>
                    <p className="text-sm text-gray-600">
                      New bookings will no longer have Zoom meetings created automatically. You can reconnect at any time.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowZoomDisconnectConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnectZoom}
                    disabled={zoomDisconnecting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {zoomDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Disconnect confirmation modal */}
          {showDisconnectConfirm && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDisconnectConfirm(false)}
            >
              <div
                className="bg-white rounded-xl p-6 shadow-xl"
                style={{ maxWidth: '400px', width: '100%' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Disconnect Google Calendar?</h3>
                    <p className="text-sm text-gray-600">
                      Bookings will no longer sync to your Google Calendar. You can reconnect at any time.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDisconnectConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnectGoogleCalendar}
                    disabled={calendarDisconnecting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {calendarDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Working Hours */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Working Hours</h2>
            <p className="text-sm text-gray-600 mb-5">
              Set your regular schedule - clients can only book during these times
            </p>

            <div className="space-y-3">
              {DAYS.map((day) => {
                const dayData = schedule[day.id] || { enabled: false, blocks: [] };
                const isEnabled = dayData.enabled;

                return (
                  <div key={day.id} className={`rounded-xl border transition-all ${isEnabled ? 'border-purple-600/30 bg-purple-50/30' : 'border-gray-200 bg-white'}`}>
                    {/* Day toggle header */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <span
                        role="checkbox"
                        aria-checked={isEnabled}
                        tabIndex={0}
                        onClick={() => toggleDay(day.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDay(day.id); } }}
                        className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-all flex-shrink-0 ${
                          isEnabled ? 'bg-purple-600' : 'border-2 border-gray-300'
                        }`}
                      >
                        {isEnabled && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleDay(day.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDay(day.id); } }}
                        className={`text-sm font-medium cursor-pointer select-none ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}
                      >
                        {day.label}
                      </span>
                      {!isEnabled && (
                        <span className="text-xs text-gray-400 ml-auto">Unavailable</span>
                      )}
                    </div>

                    {/* Time blocks */}
                    {isEnabled && (
                      <div className="px-4 pb-4 pt-1">
                        <div className="space-y-2 ml-8">
                          {dayData.blocks.map((block, blockIndex) => (
                            <div key={blockIndex} className="flex items-center gap-2">
                              <select
                                value={block.start}
                                onChange={(e) => updateBlockTime(day.id, blockIndex, 'start', e.target.value)}
                                className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none bg-white"
                              >
                                {TIME_OPTIONS.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              <span className="text-gray-400 text-xs">to</span>
                              <select
                                value={block.end}
                                onChange={(e) => updateBlockTime(day.id, blockIndex, 'end', e.target.value)}
                                className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none bg-white"
                              >
                                {TIME_OPTIONS.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              {dayData.blocks.length > 1 && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => removeBlock(day.id, blockIndex)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeBlock(day.id, blockIndex); } }}
                                  className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors ml-1"
                                  aria-label="Remove time block"
                                >
                                  <X className="w-4 h-4" />
                                </span>
                              )}
                            </div>
                          ))}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => addBlock(day.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addBlock(day.id); } }}
                            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium cursor-pointer transition-colors mt-1"
                          >
                            <Plus className="w-3 h-3" />
                            Add another time block
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 4: Session Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Session Settings</h2>

            <div className="space-y-8">
              {/* Buffer Between Sessions */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Buffer between sessions</label>
                  <p className="text-xs text-gray-500 mt-0.5">Time to reset and prepare between clients</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 0, label: 'None' },
                    { value: 15, label: '15 min' },
                    { value: 30, label: '30 min' },
                    { value: 45, label: '45 min' },
                    { value: 60, label: '1 hour' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setBufferTime(option.value);
                        setIsCustomBuffer(false);
                      }}
                      className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                        bufferTime === option.value && !isCustomBuffer
                          ? 'border-purple-600 bg-purple-600 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setIsCustomBuffer(true)}
                    className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                      isCustomBuffer
                        ? 'border-purple-600 bg-purple-600 text-white'
                        : 'border-purple-300 bg-white text-purple-600 hover:border-purple-400'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {isCustomBuffer && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      value={customBufferValue}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setCustomBufferValue(val);
                        const minutes = customBufferUnit === 'hours' ? val * 60 : val;
                        setBufferTime(minutes);
                      }}
                      min="1"
                      max={customBufferUnit === 'hours' ? 8 : 480}
                      className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      style={{ minHeight: 'auto' }}
                    />
                    <select
                      value={customBufferUnit}
                      onChange={(e) => {
                        setCustomBufferUnit(e.target.value);
                        const minutes = e.target.value === 'hours' ? customBufferValue * 60 : customBufferValue;
                        setBufferTime(minutes);
                      }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Advance Booking Window */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Advance booking window</label>
                  <p className="text-xs text-gray-500 mt-0.5">How far ahead can clients book?</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 7, label: '1 week' },
                    { value: 14, label: '2 weeks' },
                    { value: 30, label: '1 month' },
                    { value: 60, label: '2 months' },
                    { value: 90, label: '3 months' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setBookingWindow(option.value);
                        setIsCustomBooking(false);
                      }}
                      className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                        bookingWindow === option.value && !isCustomBooking
                          ? 'border-purple-600 bg-purple-600 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setIsCustomBooking(true)}
                    className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                      isCustomBooking
                        ? 'border-purple-600 bg-purple-600 text-white'
                        : 'border-purple-300 bg-white text-purple-600 hover:border-purple-400'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {isCustomBooking && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      value={customBookingValue}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setCustomBookingValue(val);
                        const days = customBookingUnit === 'months' ? val * 30 : val;
                        setBookingWindow(days);
                      }}
                      min="1"
                      max={customBookingUnit === 'months' ? 12 : 365}
                      className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      style={{ minHeight: 'auto' }}
                    />
                    <select
                      value={customBookingUnit}
                      onChange={(e) => {
                        setCustomBookingUnit(e.target.value);
                        const days = e.target.value === 'months' ? customBookingValue * 30 : customBookingValue;
                        setBookingWindow(days);
                      }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    >
                      <option value="days">days</option>
                      <option value="months">months</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Minimum Notice */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Minimum notice</label>
                  <p className="text-xs text-gray-500 mt-0.5">Prevent last-minute bookings</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 2, label: '2 hours' },
                    { value: 4, label: '4 hours' },
                    { value: 12, label: '12 hours' },
                    { value: 24, label: '24 hours' },
                    { value: 48, label: '48 hours' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setMinNotice(option.value);
                        setIsCustomNotice(false);
                      }}
                      className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                        minNotice === option.value && !isCustomNotice
                          ? 'border-purple-600 bg-purple-600 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setIsCustomNotice(true)}
                    className={`w-[115px] py-2 text-sm text-center rounded-full border-2 transition-all ${
                      isCustomNotice
                        ? 'border-purple-600 bg-purple-600 text-white'
                        : 'border-purple-300 bg-white text-purple-600 hover:border-purple-400'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {isCustomNotice && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      value={customNoticeValue}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setCustomNoticeValue(val);
                        const hours = customNoticeUnit === 'days' ? val * 24 : val;
                        setMinNotice(hours);
                      }}
                      min="1"
                      max={customNoticeUnit === 'days' ? 30 : 720}
                      className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      style={{ minHeight: 'auto' }}
                    />
                    <select
                      value={customNoticeUnit}
                      onChange={(e) => {
                        setCustomNoticeUnit(e.target.value);
                        const hours = e.target.value === 'days' ? customNoticeValue * 24 : customNoticeValue;
                        setMinNotice(hours);
                      }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 5: Block Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Block Dates</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Prevent bookings on specific dates when you're unavailable
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Blocked dates sync with your calendar and won't be visible to clients
                </p>
              </div>
              <button
                onClick={() => setShowTimeOffModal(true)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-purple-600 text-purple-600 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Block
              </button>
            </div>

            {sortedTimeOff.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No blocked dates</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedTimeOff.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">{item.title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {new Date(item.start).toLocaleDateString()} - {new Date(item.end).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteTimeOff(item.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-2"
                      aria-label="Delete time off"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Link
              to="/dashboard/settings"
              className="px-6 py-2.5 text-gray-700 hover:text-gray-900 font-medium transition-colors"
            >
              Cancel
            </Link>
            <LoadingButton
              onClick={handleSave}
              loading={isSaving}
              loadingText="Saving..."
              className="px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all flex items-center gap-2"
            >
              Save Changes
            </LoadingButton>
          </div>
        </div>
      </div>

      {/* Time Off Modal */}
      {showTimeOffModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowTimeOffModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <button
              onClick={() => setShowTimeOffModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">Block Dates</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={timeOffTitle}
                  onChange={(e) => setTimeOffTitle(e.target.value)}
                  placeholder="Vacation, Conference, Personal, etc."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={timeOffStart}
                  onChange={(e) => setTimeOffStart(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={timeOffEnd}
                  onChange={(e) => setTimeOffEnd(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowTimeOffModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTimeOff}
                disabled={!timeOffStart || !timeOffEnd}
                className="px-5 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Block
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilitySettings;
